import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  Chip, CircularProgress, Alert
} from '@mui/material';
import { AddBox, ListAlt, LocalShipping, CheckCircle, HourglassEmpty, Cancel, AttachMoney, Campaign } from '@mui/icons-material';
import CustomerLayout from '../components/CustomerLayout';
import { customerOrdersAPI } from '../services/api';
import { fetchCompanyInfo } from '../services/companyInfo';

// 금액상태 (고객 표시용)
const PRICE_STATUS_MAP = {
  pending_price:     { label: '금액검토',    color: 'default' },
  payment_pending:   { label: '결재대기',    color: 'warning' },
  deferred_payment:  { label: '추후결재',    color: 'secondary' },
  payment_completed: { label: '결재완료',    color: 'success' },
  refund_pending:    { label: '환불대기',    color: 'error' },
  refund_completed:  { label: '환불완료',    color: 'default' },
  no_payment:        { label: '결재금액없음', color: 'default' },
};

// payment_pending 은 고객 수락 전이면 '승인대기', 수락 후이면 '결재대기'
const priceStatusFor = (order) => {
  if (order.price_status === 'payment_pending') {
    return order.customer_accepted_at
      ? { label: '결재대기', color: 'warning' }
      : { label: '승인대기', color: 'info' };
  }
  return PRICE_STATUS_MAP[order.price_status] || { label: order.price_status || '-', color: 'default' };
};

// 배차상태 (고객 표시용 - 간소화)
const DISPATCH_STATUS_MAP = {
  before_dispatch: { label: '배차전',   color: 'default',  icon: <HourglassEmpty fontSize="small" /> },
  dispatch_ready:  { label: '배차대기', color: 'info',     icon: <HourglassEmpty fontSize="small" /> },
  broadcasting:    { label: '배차대기', color: 'info',     icon: <Campaign fontSize="small" /> },
  assigned:        { label: '배차완료', color: 'primary',  icon: <LocalShipping fontSize="small" /> },
  in_progress:     { label: '운송중',   color: 'primary',  icon: <LocalShipping fontSize="small" /> },
  completed:       { label: '완료',     color: 'success',  icon: <CheckCircle fontSize="small" /> },
  cancelled:       { label: '주문취소', color: 'error',    icon: <Cancel fontSize="small" /> },
};

const ACTIVE_STATUSES = ['before_dispatch', 'dispatch_ready', 'broadcasting'];
const TRANSIT_STATUSES = ['assigned', 'in_progress'];

export default function CustomerDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');

  const user = JSON.parse(localStorage.getItem('customer_user') || '{}');

  useEffect(() => {
    customerOrdersAPI.getAll()
      .then((res) => setOrders(res.data.data || []))
      .catch(() => setError('주문 정보를 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
    fetchCompanyInfo().then((c) => setCompanyName(c.company_name || '')).catch(() => {});
  }, []);

  const recentOrders = orders.slice(0, 5);
  const stats = {
    total: orders.length,
    active: orders.filter(o => ACTIVE_STATUSES.includes(o.status)).length,
    in_progress: orders.filter(o => TRANSIT_STATUSES.includes(o.status)).length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  return (
    <CustomerLayout onLogout={onLogout}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          안녕하세요, {user.company_name || user.name}님 👋
        </Typography>
        <Typography color="text.secondary">
          {companyName ? `${companyName} 배차 서비스를 이용해 주셔서 감사합니다.` : '배차 서비스를 이용해 주셔서 감사합니다.'}
        </Typography>
      </Box>

      {/* 통계 카드 */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: '전체 주문', value: stats.total,       color: '#1565c0', bg: '#e3f2fd' },
          { label: '접수중',   value: stats.active,      color: '#e65100', bg: '#fff3e0' },
          { label: '운송중',   value: stats.in_progress, color: '#0277bd', bg: '#e1f5fe' },
          { label: '완료',     value: stats.completed,   color: '#2e7d32', bg: '#e8f5e9' },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ bgcolor: stat.bg, boxShadow: 'none', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 빠른 메뉴 */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card
            sx={{ cursor: 'pointer', borderRadius: 2, border: '2px solid #1565c0', '&:hover': { bgcolor: '#e3f2fd' } }}
            onClick={() => navigate('/orders/new')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AddBox sx={{ fontSize: 40, color: '#1565c0' }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>배차 주문하기</Typography>
                <Typography variant="body2" color="text.secondary">새로운 배차를 신청합니다</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card
            sx={{ cursor: 'pointer', borderRadius: 2, border: '2px solid #e65100', '&:hover': { bgcolor: '#fff3e0' } }}
            onClick={() => navigate('/orders')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ListAlt sx={{ fontSize: 40, color: '#e65100' }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>내 주문 목록</Typography>
                <Typography variant="body2" color="text.secondary">주문 현황을 확인합니다</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 최근 주문 */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>최근 주문</Typography>
          <Button size="small" onClick={() => navigate('/orders')}>전체보기</Button>
        </Box>

        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && recentOrders.length === 0 && (
          <Card sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">아직 주문이 없습니다.</Typography>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/orders/new')}>
              첫 주문하기
            </Button>
          </Card>
        )}
        {recentOrders.map((order) => {
          const ds = DISPATCH_STATUS_MAP[order.status] || { label: order.status, color: 'default', icon: <HourglassEmpty fontSize="small" /> };
          const ps = priceStatusFor(order);
          return (
            <Card key={order.order_id} sx={{ mb: 1.5, borderRadius: 2, '&:hover': { boxShadow: 3 } }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={700} color="primary">
                    {order.order_number}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip label={ps.label} color={ps.color} size="small" variant="outlined" />
                    <Chip icon={ds.icon} label={ds.label} color={ds.color} size="small" />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {order.origin_address} → {order.destination_address}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {new Date(order.created_at).toLocaleDateString('ko-KR')}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </CustomerLayout>
  );
}
